import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useDemoData } from "@/hooks/useDemoData";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function DetailsModal({
  type,
  onClose,
}: {
  type: string;
  onClose: () => void;
}) {
  const { loads, bids } = useDemoData();
  const [expandedLoads, setExpandedLoads] = useState<string[]>([]);

  const toggleLoad = (loadId: string) => {
    setExpandedLoads(prev => 
      prev.includes(loadId) 
        ? prev.filter(id => id !== loadId)
        : [...prev, loadId]
    );
  };

  if (type === "recent-bids") {
    // Group bids by loadID
    const groupedBids = bids.reduce((acc, bid) => {
      if (!acc[bid.loadID]) {
        acc[bid.loadID] = [];
      }
      acc[bid.loadID].push(bid);
      return acc;
    }, {} as Record<string, typeof bids>);

    // Sort bids within each group by amount (highest first)
    Object.keys(groupedBids).forEach(loadId => {
      groupedBids[loadId].sort((a, b) => b.amount - a.amount);
    });

    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Recent Bids</DialogTitle>
            <DialogDescription>
              Click on a load to see all bids
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white shadow-sm z-10">
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Load</th>
                    <th className="text-left p-3 font-medium">Current Amount</th>
                    <th className="text-left p-3 font-medium">Total Bids</th>
                    <th className="text-left p-3 font-medium">Latest Date</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedBids).map(([loadId, loadBids]) => {
                    const currentBid = loadBids[0]; // Highest bid
                    const isExpanded = expandedLoads.includes(loadId);
                    
                    return (
                      <React.Fragment key={loadId}>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer border-b"
                          onClick={() => toggleLoad(loadId)}
                        >
                          <td className="p-3">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 inline mr-2" />
                            ) : (
                              <ChevronRight className="h-4 w-4 inline mr-2" />
                            )}
                            {loadId}
                          </td>
                          <td className="p-3 font-semibold text-green-600">
                            ${currentBid.amount}
                          </td>
                          <td className="p-3">{loadBids.length}</td>
                          <td className="p-3">
                            {new Date(currentBid.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={4} className="p-0">
                              <div className="bg-gray-50 border-l-4 border-blue-200">
                                <div className="p-4">
                                  <h4 className="font-medium mb-3 text-sm text-gray-700">
                                    All Bids for {loadId}
                                  </h4>
                                  <div className="space-y-2">
                                    {loadBids.map((bid, index) => (
                                      <div 
                                        key={bid.id} 
                                        className={`flex justify-between items-center p-3 rounded ${
                                          index === 0 ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-200'
                                        }`}
                                      >
                                        <div className="flex items-center space-x-4">
                                          <span className="text-sm font-mono">{bid.id}</span>
                                          <span className={`font-semibold ${
                                            index === 0 ? 'text-green-700' : 'text-gray-700'
                                          }`}>
                                            ${bid.amount}
                                          </span>
                                          <span className="text-sm text-gray-500">
                                            {new Date(bid.createdAt).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <div>
                                          {index === 0 ? (
                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                              Current Winner
                                            </span>
                                          ) : (
                                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                                              Outbid
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (type === "active-loads" || type === "completed-loads") {
    const isActive = type === "active-loads";
    const list = loads.filter(
      (load) =>
        load.carrierID && new Date(load.deliveryEnd) > new Date() === isActive
    );
    
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{isActive ? "Active" : "Completed"} Loads</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white shadow-sm z-10">
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Load</th>
                    <th className="text-left p-3 font-medium">From→To</th>
                    <th className="text-left p-3 font-medium">Pieces</th>
                    <th className="text-left p-3 font-medium">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((l) => (
                    <tr key={l.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{l.id}</td>
                      <td className="p-3">{l.source}→{l.destination}</td>
                      <td className="p-3">{l.pieces}</td>
                      <td className="p-3">{l.weight}</td>
                    </tr>
                  ))}
                  {list.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-gray-500 p-8">
                        No {isActive ? "active" : "completed"} loads found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return null;
}